/**
 * The seam that lets the shared object peek render EDITABLE fields in place
 * without pulling a server action or the field control into `@excellent/ui`.
 *
 * `ObjectPeek` (this package) owns the layout — which rows sit in the property
 * rail, which prose sits in the body — but it cannot own the WRITE: the control
 * is a shell component and the write is a shell server action. So the shell wraps
 * the peek in a provider that owns the per-field write state (drafts, phase, the
 * one gated-option note) and hands back a finished `PeekField` per descriptor;
 * the peek places it. Absent a provider, the peek renders the read-only rail
 * exactly as before — this is purely additive.
 *
 * The provider returns a WHOLE `PeekField` (not just a control) so the full-width
 * `status` slot — the note line's home — stays with the value it belongs to. A
 * self-contained per-cell editor could only fill the `control` slot, which is the
 * ~140px trap the status slot was added to escape.
 */

import * as React from "react";
import type { ObjectField } from "../api/contracts/objects";
import type { PeekField } from "./peek-dialog";

export interface ObjectEditContextValue {
  /**
   * One descriptor → a finished `PeekField`. Editable fields come back with a
   * `control` + full-width `status`; locked fields come back as a read-only row
   * that states its reason. The peek merges these into the property rail.
   *
   * `opts.chip` asks for the CHIP presentation — the control drawn compact,
   * auto-width and borderless-until-touched, for the inline Properties row
   * (`PeekDialogPropertyChips`) rather than the full-width property table. It is
   * presentation only: the same draft, write and status machinery drives both.
   */
  renderField: (field: ObjectField, opts?: { chip?: boolean }) => PeekField;
  /**
   * The IDENTITY descriptor (the object's name/title) → an editable heading node
   * for the masthead's title slot, so a rename happens WHERE the name is shown
   * instead of as a redundant rail row. `fallback` is the plain title text to
   * render when the identity field is locked (or absent). Optional: a provider
   * that doesn't implement it leaves the masthead as static text.
   */
  renderTitle?: (field: ObjectField, fallback: React.ReactNode) => React.ReactNode;
  /**
   * The identity field's write status (Saving…/Saved/error, or a lock reason) for
   * the masthead's DESCRIPTION slot — kept OUT of the DialogTitle so it never
   * corrupts the dialog's accessible name. Returns null when idle.
   */
  renderTitleStatus?: (field: ObjectField) => React.ReactNode;
  /**
   * A PROSE descriptor (`type: "longtext"`) → an edit-in-place paragraph for the
   * body's Description section (Matt, 2026-07-21: "all descriptions should also
   * be edit in place like the titles").
   *
   * Prose is the second thing on an object that is not a property, and it had
   * the same defect the name had: a `longtext` descriptor landed in the property
   * list, so a job's whole role description was edited through a 3-row textarea
   * wedged into a label→value row, under a `w-24` "Description" label, between
   * Status and Department. The fix is the same one the title got — edit it WHERE
   * it is read — which is why this is a sibling of `renderTitle` rather than a
   * variant of `renderField`.
   *
   * `fallback` is the DTO's own `description`, rendered when the kind ships no
   * prose descriptor (nothing editable) so the section still reads.
   */
  renderProse?: (field: ObjectField, fallback: React.ReactNode) => React.ReactNode;
  /**
   * The WHOLE object's write status, in one line, for the peek's static footer
   * ("Saving…", "Saved 3:42 PM", "Unsaved changes", or the failure).
   *
   * Per-field status already rides each row, and that stays — it is what tells
   * you WHICH field settled. But every edit here saves on blur, and a write the
   * user never sees is indistinguishable from one that never happened; a field's
   * own status line is easy to miss when the field is halfway up a scrolled
   * column. So the footer carries the aggregate, which is the slot that is
   * always on screen. It is a DERIVED summary of the same per-field state, not a
   * second source of truth — there is nothing here to keep in sync.
   *
   * Returns null when there is genuinely nothing to report, so a peek at rest
   * shows an empty footer slot rather than a reassuring lie.
   */
  renderSaveStatus?: () => React.ReactNode;
}

const ObjectEditContext = React.createContext<ObjectEditContextValue | null>(null);

/** The provider the shell mounts around the peek host. */
export const ObjectEditProvider = ObjectEditContext.Provider;

/** The peek reads this to fold editable fields into its rail. Null = read-only. */
export function useObjectEdit(): ObjectEditContextValue | null {
  return React.useContext(ObjectEditContext);
}
